import {
  Account,
  AccountDto,
  AccountSettingsAts,
  ATS_NAMES,
  SettingsAtsApplicationMethod,
  SettingsAtsName,
  SUPPORTED_APPLICATION_METHODS,
} from '@mkp/account/models';
import { LegalEntity } from '@mkp/legal-entity/data-access';
import { mapAuditDates } from '@mkp/shared/data-access';

// just in case we find an unknown value, should not happen
const DEFAULT_ATS = 'INTERNAL';

export const mapAccountDtoToAccount = (dto: AccountDto, legalEntity: LegalEntity): Account => ({
  id: dto.id,
  name: dto.name,
  state: dto.state,
  type: dto.type,
  legalEntity,
  ...mapAuditDates(dto),
  createdBy: dto._embedded.createdBy,
  updatedBy: dto._embedded.updatedBy ?? null,
  settings: {
    ats: {
      name: getAtsName(dto.settings.ats.name),
      config: dto.settings.ats.config,
      applicationMethod: dto.settings.ats.applicationMethod,
    },
    freeProductsAllowed: dto.settings.freeProductsAllowed,
    mfaEnabled: dto.settings.mfaEnabled,
  },
  computed: {
    canCreateUser: !!dto._links.createAccountMembership,
  },
  _version: dto._version,
  _links: dto._links,
});

const getAtsName = (atsName: string): SettingsAtsName => {
  if (!isAtsName(atsName)) {
    console.error(`Unknown ATS: ${atsName}`);
    return DEFAULT_ATS;
  }

  return atsName;
};

const isAtsName = (atsName: string): atsName is SettingsAtsName =>
  ATS_NAMES.includes(atsName as SettingsAtsName);

export function mapAtsConfigToPostBody(data: {
  ats: SettingsAtsName;
  atsConfig?: Record<string, string | boolean>;
}): AccountSettingsAts {
  const { quickApply, ...config } = data.atsConfig || {};

  const applicationMethod = getApplicationMethod(data.ats, quickApply as boolean | undefined);
  const hasConfigFields = Object.keys(config).length > 0;

  return {
    name: data.ats,
    config: hasConfigFields ? (config as AccountSettingsAts['config']) : null,
    applicationMethod,
  };
}

const getApplicationMethod = (
  ats: SettingsAtsName,
  quickApply: boolean | undefined
): SettingsAtsApplicationMethod =>
  SUPPORTED_APPLICATION_METHODS[ats] === 'INTERNAL_OR_EXTERNAL'
    ? quickApply
      ? 'INTERNAL'
      : 'EXTERNAL'
    : SUPPORTED_APPLICATION_METHODS[ats];

export function mapApplicationMethodToQuickApply(
  applicationMethod: SettingsAtsApplicationMethod
): boolean {
  return applicationMethod === 'INTERNAL';
}
